/* XD Custom Select
   ---------------------------------------------------------------------
   Replaces the native popup of any <select data-xd-select> with a
   custom-built dropdown.

   Why: styling <option> background/text color (e.g. a dark popup on a
   dark page) is not reliably supported across browsers. On Windows
   Chrome/Edge, the native popup only reliably applies our custom color
   to the currently highlighted option — every other option ends up
   with our white text on the OS's own white popup background, i.e.
   invisible. This never shows up on Mac, where WebKit/Blink honor the
   option styling, which is why it's easy to miss in testing.

   Approach: keep the real <select> in the DOM (visually hidden, not
   display:none) so it still works for keyboard and screen-reader users
   via the browser's native, unstyled — and therefore always readable —
   popup. Everything else (mouse/touch users, the vast majority) gets a
   fully custom-styled dropdown built from plain divs, so it can never
   suffer the native-popup-styling bug on any platform.

   The real <select> stays the single source of truth: picking an
   option in the custom list just sets select.value and dispatches a
   real "change" event, so any existing code listening for that event
   keeps working unmodified.

   The popup list is appended to <body> and positioned with
   position:fixed (coordinates computed fresh on every open), instead of
   living next to its trigger. Some of the sections this enhances put
   the trigger inside a container with overflow:hidden — needed for
   THAT container's own purpose (e.g. clipping a swipe carousel's track
   during its slide animation) — and a popup living inside would get
   silently clipped by that same boundary the instant it grew past it,
   no matter how much actual screen space was free below. Living in
   <body> sidesteps every such ancestor, present or future, instead of
   us having to special-case each one we discover.
   ---------------------------------------------------------------------
*/
(function () {
  if (window.__xdSelectInit) return;
  window.__xdSelectInit = true;

  function closeAll(except) {
    document.querySelectorAll('.xd-csel-list.xd-csel-open').forEach(function (list) {
      if (list !== except) list.classList.remove('xd-csel-open');
    });
  }

  function refresh(select) {
    var refs = select._xdRefs;
    if (!refs) return;
    var opt = select.options[select.selectedIndex];
    refs.display.textContent = opt ? opt.textContent.trim() : '';
    var items = refs.list.children;
    for (var i = 0; i < items.length; i++) {
      var match = items[i].getAttribute('data-value') === select.value;
      items[i].classList.toggle('xd-csel-opt-active', match);
      items[i].setAttribute('aria-selected', match ? 'true' : 'false');
    }
  }

  function enhance(select) {
    if (select._xdRefs) return;

    /* Hide the native control visually only — stays focusable/operable
       for keyboard + screen reader users, and its own popup (no custom
       option colors applied here) is always readable as a fallback. */
    select.style.position = 'absolute';
    select.style.width = '1px';
    select.style.height = '1px';
    select.style.padding = '0';
    select.style.margin = '-1px';
    select.style.overflow = 'hidden';
    select.style.clip = 'rect(0,0,0,0)';
    select.style.whiteSpace = 'nowrap';
    select.style.border = '0';

    var wrap = select.parentElement;
    if (wrap && getComputedStyle(wrap).position === 'static') {
      wrap.style.position = 'relative';
    }

    /* Reuse the select's own class on the trigger div so it automatically
       picks up that section's existing color/border/padding/height/font
       (and mobile overrides) instead of every enhanced select converging
       on one hardcoded look. Same idea for the arrow via
       data-xd-arrow-class, since arrow color/position is themeable too
       in some sections. */
    var display = document.createElement('div');
    display.className = select.className + ' xd-csel-display';
    display.setAttribute('aria-hidden', 'true');

    var arrowExtraClass = select.getAttribute('data-xd-arrow-class') || '';
    var arrow = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    arrow.setAttribute('class', ('xd-csel-arrow ' + arrowExtraClass).trim());
    arrow.setAttribute('viewBox', '0 0 10 6');
    arrow.setAttribute('aria-hidden', 'true');
    arrow.innerHTML = '<path d="M1 1l4 4 4-4"/>';

    /* Lives in <body>, positioned with position:fixed — see file header
       for why. Coordinates are computed fresh in toggle() every time it
       opens, so it always lines up with its trigger regardless of where
       that trigger ends up moving on the page between opens. */
    var list = document.createElement('ul');
    list.className = 'xd-csel-list';
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-hidden', 'true');

    Array.prototype.forEach.call(select.options, function (opt) {
      var li = document.createElement('li');
      li.className = 'xd-csel-opt';
      li.setAttribute('role', 'option');
      li.setAttribute('data-value', opt.value);
      li.textContent = opt.textContent.trim();
      li.addEventListener('click', function () {
        if (select.value !== opt.value) {
          select.value = opt.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        refresh(select);
        list.classList.remove('xd-csel-open');
      });
      list.appendChild(li);
    });

    select.insertAdjacentElement('afterend', display);
    display.insertAdjacentElement('afterend', arrow);
    document.body.appendChild(list);

    function position() {
      var margin = 8;
      list.classList.remove('xd-csel-list--up');
      list.style.left = '';
      list.style.right = '';
      list.style.top = '';
      list.style.bottom = '';
      list.style.maxHeight = '';
      list.style.maxWidth = '';

      var triggerRect = display.getBoundingClientRect();
      var vh = window.innerHeight;
      var vw = window.innerWidth;
      var spaceBelow = vh - triggerRect.bottom - margin;
      var spaceAbove = triggerRect.top - margin;

      /* getBoundingClientRect() is viewport-relative, the same coordinate
         space position:fixed uses — no scrollX/scrollY math needed. */
      list.style.minWidth = triggerRect.width + 'px';
      list.style.left = triggerRect.left + 'px';

      /* Vertical: prefer opening down; only flip up if space below is
         thin AND above genuinely has more room. Deliberately based on
         trigger POSITION alone (a fixed minimum), not on this list's own
         content height — two selects sitting side by side in the same
         row have near-identical space above/below, but can have very
         different option counts (e.g. 2 materials vs 5 sizes). Basing
         the decision on content height made them flip independently,
         so the same row could open one dropdown up and the other down.
         Capping max-height to whatever room actually exists (below)
         still guarantees no visual overflow even when a tall list picks
         the "wrong" side and has to scroll internally. */
      var MIN_USEFUL_SPACE = 160;
      var openUp = spaceBelow < MIN_USEFUL_SPACE && spaceAbove > spaceBelow;
      list.classList.toggle('xd-csel-list--up', openUp);
      if (openUp) {
        list.style.bottom = (vh - triggerRect.top + 6) + 'px';
        list.style.maxHeight = Math.max(60, spaceAbove) + 'px';
      } else {
        list.style.top = (triggerRect.bottom + 6) + 'px';
        list.style.maxHeight = Math.max(60, spaceBelow) + 'px';
      }

      /* Horizontal: the list can be wider than its trigger (option text
         is untruncated), so a trigger sitting near the right edge of the
         screen can push it past the viewport. Flip to right-aligned when
         that would happen, and cap max-width as a hard backstop either
         way. */
      var listRect = list.getBoundingClientRect();
      if (listRect.right > vw - margin) {
        list.style.left = '';
        list.style.right = (vw - triggerRect.right) + 'px';
      }
      list.style.maxWidth = (vw - margin * 2) + 'px';
    }

    function toggle(e) {
      e.preventDefault();
      var willOpen = !list.classList.contains('xd-csel-open');
      closeAll(willOpen ? list : null);
      if (!willOpen) {
        list.classList.remove('xd-csel-open');
        return;
      }
      list.classList.add('xd-csel-open'); // becomes measurable now
      position();
    }
    display.addEventListener('click', toggle);
    arrow.addEventListener('click', toggle);

    select._xdRefs = { display: display, arrow: arrow, list: list };
    select.addEventListener('change', function () { refresh(select); });

    refresh(select);
  }

  function enhanceAll(root) {
    (root || document).querySelectorAll('select[data-xd-select]').forEach(enhance);
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('.xd-csel-list, .xd-csel-display, .xd-csel-arrow')) return;
    closeAll(null);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAll(null);
  });
  /* The popup is positioned with fixed pixel coordinates computed at open
     time, not tied to the trigger via CSS layout (that's the whole point —
     it lives in <body> now, see enhance() above). If the page scrolls or
     resizes while it's open it would stay frozen in place and visually
     detach from its trigger, so just close it instead of trying to track
     the trigger continuously. capture:true catches scrolling on any
     scrollable ancestor, not only the window. */
  window.addEventListener('scroll', function () { closeAll(null); }, { passive: true, capture: true });
  window.addEventListener('resize', function () { closeAll(null); }, { passive: true });

  window.XDSelect = { enhance: enhance, enhanceAll: enhanceAll, refresh: refresh };

  enhanceAll(document);
  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('select[data-xd-select]')) enhance(node);
          if (node.querySelectorAll) enhanceAll(node);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
